import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';

import ProfileIcon from '../../assets/icons/profile.svg';
import SettingsIcon from '../../assets/icons/settings.svg';
import LogoutIcon from '../../assets/icons/logout.svg';
import { logoutUsuario } from '@/api/auth.api';
import { ROLES } from '@/constants/roles';
import { ROTAS } from '@/routes/routePaths';

const UserProfileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { usuario } = useAuth();

  useEffect(() => {

  }, [usuario]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleLogout = async () => {
    try {
      await logoutUsuario();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getInitials = (name: string | undefined) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const userName = usuario?.nome || 'Usuário TugasCoins';
  const userEmail = usuario?.email || 'email@TugasCoins.com';
  const userType = usuario?.tipo_usuario;
  const userInitials = getInitials(userName);

  // Menu específico para administradores
  const adminMenuItems = [
    { name: 'Dashboard Admin', onClick: () => navigate(ROTAS.DASHBOARD_ADMIN) },
    { name: 'Gestão de Usuários', onClick: () => navigate(ROTAS.GERENCIAMENTO_USUARIOS) },
    { name: 'Gestão de Clientes', onClick: () => navigate(ROTAS.GERENCIAMENTO_CLIENTES) },
    { name: 'Relatórios', onClick: () => navigate(ROTAS.RELATORIOS) },
  ];

  // Menu específico para operadores (caixa)
  const operatorMenuItems = [
    { name: 'Dashboard Caixa', onClick: () => navigate(ROTAS.DASHBOARD_CAIXA) },
  ];

  // Menu específico para clientes
  const clientMenuItems = [
    { name: 'Home', onClick: () => navigate(ROTAS.DASHBOARD_CLIENTE) },
    { name: 'Histórico', onClick: () => navigate(ROTAS.HISTORICO_TRANSACOES) },
  ];

  // Combinar menus baseado no tipo de usuário
  let menuItems = [];

  if (usuario?.tipo_usuario === ROLES.ADMINISTRADOR) {
    menuItems = [ ...adminMenuItems];
  } else if (userType === ROLES.OPERADOR) {
    menuItems = [ ...operatorMenuItems];
  } else {
    menuItems = [ ...clientMenuItems];
  }

  // Adicionar logout no final
  menuItems.push({ name: 'Sair', onClick: handleLogout, isDanger: true });

  // Função para obter o label do tipo de usuário
  const getUserTypeLabel = (type: string | null) => {
    switch (type) {
      case ROLES.ADMINISTRADOR:
        return 'Administrador';
      case ROLES.OPERADOR:
        return 'Operador de Caixa';
      case null:
        return 'Cliente';
      default:
        return 'Usuário';
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="flex items-center space-x-2 p-2 rounded-full transition-all duration-200 ease-in-out bg-background hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#cebc6e] hover:to-[#c5a663] "
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] flex items-center justify-center text-white font-bold text-lg shadow-md">
          {userInitials}
        </div>
        <div className="hidden md:flex flex-col items-start text-left">
          <span className="text-text text-sm font-semibold">{userName}</span>
          <span className="text-text/70 text-xs">{userEmail}</span>
          <span className="text-text/50 text-xs">{getUserTypeLabel(usuario?.tipo_usuario)}</span>
        </div>
        <svg
          className={`w-4 h-4 text-text transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-64 bg-background border border-primary rounded-lg shadow-xl overflow-hidden z-50 transform origin-top-right max-h-96 overflow-y-auto"
          >
            <div className="p-4 border-b border-primary">
              <p className="text-text text-base font-semibold">{userName}</p>
              <p className="text-text/70 text-sm">{userEmail}</p>
              <p className="text-text/50 text-xs">{getUserTypeLabel(usuario?.tipo_usuario)}</p>
            </div>
            <nav className="py-1">
              {menuItems.map((item, index) => (
                <button
                  key={`${item.name}-${index}`}
                  onClick={() => {
                    item.onClick();
                    setIsOpen(false);
                  }}
                  className={`w-full justify-start px-4 py-3 text-start flex items-center text-sm bg-background transition-colors duration-200 ${
                    item.isDanger 
                      ? 'text-danger hover:bg-danger/20' 
                      : 'text-text hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#9c8f54] hover:to-[#c5a663]'
                  }`}
                  role="menuitem"
                >
                {item.isDanger && <img 
                    src={LogoutIcon} 
                    alt={item.name} 
                    className="w-5 h-5 mr-3"
                  />}
                  {item.name}
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfileMenu;